import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import '../../src/style/Auth/GoogleButton.css';

const GoogleButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const abortController = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  const isRegisterPage = location.pathname === '/register';

  const showErrorModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

const handleSuccess = async (credentialResponse) => {
  console.log('🔐 GOOGLE AUTH STARTED');
  
  abortController.current = new AbortController();

  try {
    const payload = {
      token: credentialResponse.credential,
      action: isRegisterPage ? 'register' : 'login',
      rememberMe: rememberMe
    };

    if (isRegisterPage) {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      if (refCode) {
        payload.referral_code = refCode;
      }
    }

    console.log('📤 Sending to backend...');
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/auth/google`,
      payload,
      {
        signal: abortController.current.signal,
        timeout: 15000,
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );

    console.log('✅ Backend response received');
    
    // ⚠️ PERBAIKAN: Validasi response lebih ketat
    if (res.data?.token && res.data?.user) {
      console.log('🔐 Calling login function with rememberMe:', rememberMe);
      
      // Pastikan token dan user valid
      if (!res.data.token || !res.data.user.id) {
        throw new Error('Invalid response from server');
      }
      
      login(res.data.user, res.data.token, rememberMe);
      
      console.log('📍 Redirecting to dashboard...');
      
      // Immediate redirect
      if (res.data.user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      console.error('❌ Invalid response format from backend');
      toast.error('Authentication failed - invalid response');
    }
  } catch (err) {
      if (!axios.isCancel(err)) {
        const errorData = err.response?.data;
        console.error('💥 Google auth error:', {
          status: err.response?.status,
          code: errorData?.code,
          message: errorData?.error
        });

        if (errorData?.code === 'ACCOUNT_NOT_REGISTERED') {
          showErrorModal('Account not registered. Please register first.');
          toast.error('Account not registered. Please register first.');
          setTimeout(() => navigate('/register'), 2000);
        } else if (errorData?.code === 'PENDING_APPROVAL') {
          if (isRegisterPage) {
            toast.success('Registration successful! Your account is pending admin approval.');
            navigate('/account-checking', { 
              state: { 
                email: errorData.email,
                isActive: false 
              } 
            });
          } else {
            toast.info('Your account is pending admin approval. Please wait for approval.');
          }
        } else {
          const errorMessage = errorData?.error || 'Google authentication failed';
          toast.error(errorMessage);
          showErrorModal(errorMessage);
        }
      }
    }
  };

  return (
    <>
      <div className="google-auth-wrapper">
        {/* HIDDEN REMEMBERME - using default true */}
        {!isRegisterPage && (
          <div style={{ display: 'none' }}>
            <input 
              type="checkbox" 
              checked={rememberMe} 
              onChange={(e) => setRememberMe(e.target.checked)}
            />
          </div>
        )}
        
        <GoogleOAuthProvider 
          clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          onScriptLoadError={() => {
            console.error('Failed to load Google script');
            toast.error('Failed to load Google authentication');
          }}
        >
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => {
              console.error('Google login failed');
              toast.error('Google login failed');
            }}
            useOneTap={!isRegisterPage}
            auto_select={!isRegisterPage}
            text={isRegisterPage ? "signup_with" : "signin_with"}
            cancel_on_tap_outside={false}
            ux_mode="popup"
          />
        </GoogleOAuthProvider>
      </div>

      {showModal && (
        <div className="modal-overlay-gbutton">
          <div className="modal-content-gbutton">
            <div className="modal-header-gbutton">
              <h3>Authentication Notice</h3>
              <button className="modal-close-gbutton" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={closeModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleButton;